import React, { createContext, useEffect, useRef, useState } from 'react';
import { Checkbox, DatePicker, IconButton, RadioButton, RadioButtonGroup, RaisedButton, TextField } from 'material-ui';
import moment from 'moment';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Pagination from 'components/Libs/Pagination';
import RemoveCircle from 'material-ui/svg-icons/content/remove-circle-outline';
import { fromUTC, toUTC } from 'helpers/dates';
import CompaniesAutoComplete from 'components/common/CompaniesAutoComplete';
import { getIssues, updateIssueStatus } from 'actions/issues';
import IssueItem from './IssueItem';
import { getSocketconnection } from './helpers';

const rowsPerPage = [25, 50, 100];

const minDate = moment([2017, 10, 3]).toDate();
const maxDate = moment().toDate();

const defaultStatus = 'all';
const STATUSES = {
    all: 'All', 
    new: 'New', 
    inProgress: 'In progress',
    confirmed: 'Confirmed',
    fixed: 'Fixed', 
    notBug: 'Not bug',
};

const getDefaultFilters = () => {
    return {
        statuses: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        company: undefined,
        withAttachments: undefined,
        takenByMe: undefined,
        topics: undefined,
    };
};

export const UserContext = createContext({});

function IssuesList(props) {
    const [issues, setIssues] = useState([]);
    const issuesRef = useRef(issues);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [numberOfRows, setNumberOfRows] = useState(25);
    const [filters, setFilters] = useState(getDefaultFilters());
    const [resetCompanyInput, setResetCompanyInput] = useState(false);

    React.useEffect(() => {
        // trigger capture any issues change (run on every rerender)
        // that prevent keeping old issues state 
        issuesRef.current = issues;
    });

    /**
     * 
     * @param {UpdateIssueMessage} message 
     * @param {any[]} issuesCurrent 
     */
    const messageHandler = (message, issuesCurrent) => {
        const { id, status, user } = message;
        const foundIssueIndex = issuesCurrent.findIndex(i => i._id === id);

        if (foundIssueIndex >= 0) {
            const clones = _.cloneDeep(issuesCurrent);

            clones[foundIssueIndex] = {
                ...clones[foundIssueIndex],
                status,
                statusBy: user,
            };

            setIssues(clones);
        }
    }

    useEffect(() => {
        const socket = getSocketconnection(props);

        /**
         * uses ref to address the last version of issues state
         * @param {UpdateIssueMessage} message 
         */
        const handler = message => messageHandler(message, issuesRef.current);

        socket.on('admin:newIssueChanges', handler);
    }, []);

    useEffect(() => {
        setIssues(props.issues);
        setTotal(props.total);
    }, [props.issues]);

    useEffect(() => {
        props.getIssues({
            page,
            limit: numberOfRows,
            filters,
        });
    }, [page, numberOfRows, filters]);

    const handleChangeSearch = (_e, v) => {
        setSearch(v);
    };

    const handleSearch = () => {
        setPage(1);
        setFilters({
            ...filters,
            topics: search || undefined,
        });
    };

    const changeStatus = (_e, value) => {
        setPage(1);
        setFilters({
            ...filters,
            statuses: value !== 'all' ? [value] : undefined,
        });
    };

    const handleUpdateRow = ({ page: p, numberOfRows: n }) => {
        setPage(numberOfRows !== n ? 1 : p);
        setNumberOfRows(n);
    };

    const handleCheckWithAttachments = (_e, withAttachments) => {
        setPage(1);
        setFilters({
            ...filters,
            withAttachments,
        });
    };

    const handleCheckTakenByMe = (_e, takenByMe) => {
        setPage(1);
        setFilters({
            ...filters,
            takenByMe,
        });
    };

    /**
     * 
     * @param {'dateFrom' | 'dateTo'} type 
     * @param {Date} value 
     */
    const handleStartDateFilter = (type, value) => {
        setFilters({
            ...filters,
            [type]: type === 'dateFrom' ? toUTC(value) : toUTC(value.setHours(23, 59, 59, 999)),
        });
    };

    /**
     * 
     * @param {{ item: { _id: string, name: string } } | undefined} [value] 
     */
    const onCompanySelected = (value) => {
        setPage(1);

        if (!value) {
            setResetCompanyInput(true);
            setTimeout(() => setResetCompanyInput(false), 100);
        }

        setFilters({
            ...filters,
            company: value ? value.item._id : value,
        });
    };

    /**
     * 
     * @param {'dateFrom' | 'dateTo'} type 
     */
    const handleRemoveDate = (type) => {

        setFilters({
            ...filters,
            [type]: undefined,
        });
    };

    const handleChangeIssue = (issueIndex, { status, _id }) => {
        const clones = _.cloneDeep(issues);

        clones[issueIndex] = {
            ...clones[issueIndex],
            status,
            statusBy: props.user,
        };

        props.updateIssueStatus(_id, status);

        setIssues(clones);
    }

    return (
        <div id='issue-reports'>
            <div className='container'>
                <h2 className='name'>ISSUE REPORTS</h2>

                <div className='issues-header'>
                    <div className='issues-header-item'>
                        <TextField
                            defaultValue={search}
                            hintText='Search'
                            onChange={handleChangeSearch}
                            onKeyPress={(event) => {
                                if (event.charCode === 13) {
                                    event.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                        <RaisedButton
                            label='Search'
                            style={{ margin: '0 0 0 20px' }}
                            primary
                            onClick={handleSearch}
                        />
                    </div>
                    <div className='issues-header-item'>
                        <Pagination
                            total={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            numberOfRows={numberOfRows}
                            updateRows={handleUpdateRow}
                        />
                    </div>
                </div>
                <div className='issues-info'>
                    <div className='issues-info-item'>
                        TOTAL:  { total }
                    </div>
                    <div className='issues-info-item'>
                        PAGES:  { Math.ceil(total / numberOfRows) }
                    </div>
                </div>
                <div className='issues-filters'>
                    <div className='filter filter-statuses'>
                        Filter by Status:
                        <RadioButtonGroup
                            onChange={changeStatus}
                            className='statuses'
                            name='type-mail'
                            defaultSelected={defaultStatus}
                        >
                            {
                                Object.entries(STATUSES).map(([value, label]) => {
                                    return (
                                        <RadioButton
                                            key={value}
                                            className='statuses-item'
                                            iconStyle={{ marginRight: '4px' }}
                                            value={value}
                                            label={label}
                                        />
                                    );
                                })
                            }
                        </RadioButtonGroup>
                        <div className='filter'>
                            <div className='filter-checkboxes'>
                                <Checkbox
                                    label='Only with attachments'
                                    checked={filters.withAttachments}
                                    onCheck={handleCheckWithAttachments}
                                />
                                <Checkbox
                                    label='Issue reports taken by me'
                                    checked={filters.takenByMe}
                                    onCheck={handleCheckTakenByMe}
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className='filter filter-company'>
                        <CompaniesAutoComplete
                            onCompanySelected={onCompanySelected}
                            resetInput={resetCompanyInput}
                            style={{ width: '256px' }}
                            hintStyle={{ fontSize: '16px' }}
                            onlyWithOwner
                        />
                        <IconButton
                            style={{ margin: '2px 0 0 -7px' }}
                            disabled={!filters.company}
                            onClick={() => onCompanySelected()}
                        >
                            <RemoveCircle
                                color={filters.company ? 'red' : 'black'}
                            />
                        </IconButton>
                    </div>
                    <div className='filter filter-date-inputs'>
                        <DatePicker
                            floatingLabelText='Filter start date:'
                            autoOk
                            minDate={minDate}
                            maxDate={maxDate}
                            textFieldStyle={{ width: '120px' }}
                            shouldDisableDate={dateSelected => dateSelected.getTime() > new Date(filters.dateTo).getTime()}
                            onChange={(_e, d) => handleStartDateFilter('dateFrom', d)}
                            value={filters.dateFrom ? fromUTC(filters.dateFrom) : undefined}
                            formatDate={date => moment(date).format('DD-MM-YYYY')}
                        />
                        <IconButton
                            style={{ margin: '25px 7px 0 -7px' }}
                            disabled={!filters.dateFrom}
                            onClick={() => handleRemoveDate('dateFrom')}
                        >
                            <RemoveCircle
                                color={filters.dateFrom ? 'red' : 'black'}
                            />
                        </IconButton>
                        <DatePicker 
                            floatingLabelText='Filter end date:'
                            autoOk
                            minDate={minDate}
                            maxDate={maxDate}
                            textFieldStyle={{ width: '120px' }}
                            
                            shouldDisableDate={dateSelected => dateSelected.getTime() < new Date(filters.dateFrom).getTime()}

                            onChange={(_e, d) => handleStartDateFilter('dateTo', d)}
                            value={filters.dateTo ? fromUTC(filters.dateTo) : undefined}
                            formatDate={date => moment(date).format('DD-MM-YYYY')}
                        />
                        <IconButton
                            style={{ margin: '25px 0 0 -7px' }}
                            disabled={!filters.dateTo}
                            onClick={() => handleRemoveDate('dateTo')}
                        >
                            <RemoveCircle
                                color={filters.dateTo ? 'red' : 'black'}
                            />
                        </IconButton>
                    </div>
                </div>
                <div>
                    <UserContext.Provider value={props.user}>
                        <ul className='issues-list'>
                            {
                                issues.map((issue, index) => 
                                    <IssueItem
                                        key={issue._id} 
                                        index={index}
                                        issue={issue} 
                                        handleChangeIssue={handleChangeIssue} 
                                    />
                                )
                            }
                        </ul>
                    </UserContext.Provider>
                </div>
            </div>
        </div>
    );
}

export default connect(
    ({ issues, user: { user } }) => {
        return {
            isLoaded: issues.isLoaded,
            issues: issues.data,
            total: issues.count ? issues.count.total : 0,
            user: _.pick(user, ['_id', 'email']),
            userId: user && user._id,
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                getIssues,
                updateIssueStatus,
            },
        dispatch
      )
)(IssuesList);

/**
 * @typedef {Object} UpdateIssueMessage
 * @property {string} id
 * @property {string} status 
 * @property {User} user
 */

/**
 * @typedef {Object} User
 * @property {string} _id
 * @property {string} email
 */
  


// WEBPACK FOOTER //
// ./app/components/Issues/List/List.jsx